import { z } from 'zod';

/**
 * Normalized administrative-boundary levels shared across layers.
 *
 * `admin_level` is decoupled from country-specific OSM admin_level numbers:
 *   1 = region (voivodeship / state-equivalent)
 *   2 = county (powiat / district-equivalent)
 *   3 = selectable sub-city administrative area (generic; NOT Warsaw-specific)
 *
 * Level 3 identifies generic, user-selectable areas used for filtering — it
 * does not encode hierarchy or a Warsaw-specific type. The first level-3
 * dataset is the 18 Warsaw districts (see
 * packages/db/src/seed/selectable-areas-seed.ts).
 *
 * Used by the `admin_boundaries` table (@ooh/db) and the boundary resolver.
 */
export const ADMIN_LEVEL_REGION = 1 as const;
export const ADMIN_LEVEL_COUNTY = 2 as const;
export const ADMIN_LEVEL_AREA = 3 as const;

export type AdminLevel =
  | typeof ADMIN_LEVEL_REGION
  | typeof ADMIN_LEVEL_COUNTY
  | typeof ADMIN_LEVEL_AREA;

// ─── Queue name constant shared between api and worker packages ───────────────
//
// Mirrors TRANSPORT_LAYER_IMPORT_QUEUE_NAME. The API enqueues onto this queue
// and the worker registers its processor against the same name.

export const ADMIN_BOUNDARY_IMPORT_QUEUE_NAME = 'admin-boundary-import';

// ─── Admin boundary import trigger ────────────────────────────────────────────

export const AdminBoundaryImportTriggerResponseSchema = z.object({
  jobId: z.string(),
});

export type AdminBoundaryImportTriggerResponse = z.infer<
  typeof AdminBoundaryImportTriggerResponseSchema
>;

// ─── Admin boundary import status ─────────────────────────────────────────────
//
// Mirrors TransportLayerImportStatusSchema. `inserted` / `skipped` are populated
// from the completed BullMQ job's return value; they are null while running or
// when no job has completed yet.

export const AdminBoundaryImportStatusSchema = z.object({
  status: z.enum(['idle', 'running', 'completed', 'failed']),
  lastRunAt: z.string().datetime().nullable(),
  lastError: z.string().nullable(),
  lastJobId: z.string().nullable(),
  inserted: z.number().int().nullable(),
  skipped: z.number().int().nullable(),
});

export type AdminBoundaryImportStatus = z.infer<typeof AdminBoundaryImportStatusSchema>;

// ─── Administrative-area geometry read endpoint (administrative-area-filtering) ─
//
// Bbox-scoped GeoJSON geometry for selectable administrative areas (e.g. Warsaw
// districts), mirroring the transport-lines bbox pattern. See
// packages/db/src/queries/admin-boundaries/list-admin-boundaries-geojson.ts for
// the query this endpoint's service consumes.

/** GeoJSON Polygon or MultiPolygon geometry, matching PostGIS ST_AsGeoJSON output. */
export const AdminBoundaryGeometrySchema = z.union([
  z.object({
    type: z.literal('Polygon'),
    coordinates: z.array(z.array(z.tuple([z.number(), z.number()]))),
  }),
  z.object({
    type: z.literal('MultiPolygon'),
    coordinates: z.array(z.array(z.array(z.tuple([z.number(), z.number()])))),
  }),
]);

export type AdminBoundaryGeometry = z.infer<typeof AdminBoundaryGeometrySchema>;

/** A single selectable administrative-area GeoJSON feature. */
export const AdminBoundaryFeatureSchema = z.object({
  type: z.literal('Feature'),
  properties: z.object({
    id: z.string(),
    name: z.string(),
  }),
  geometry: AdminBoundaryGeometrySchema,
});

export type AdminBoundaryFeature = z.infer<typeof AdminBoundaryFeatureSchema>;

export const AdminBoundaryFeatureCollectionSchema = z.object({
  type: z.literal('FeatureCollection'),
  features: z.array(AdminBoundaryFeatureSchema),
});

export type AdminBoundaryFeatureCollection = z.infer<typeof AdminBoundaryFeatureCollectionSchema>;

const LONGITUDE_MIN = -180;
const LONGITUDE_MAX = 180;
const LATITUDE_MIN = -90;
const LATITUDE_MAX = 90;

interface RawBboxCorners {
  minLon: number;
  minLat: number;
  maxLon: number;
  maxLat: number;
}

/** Appends a range-violation issue for a single bbox corner field, if out of bounds. */
function checkCornerRange(
  ctx: z.RefinementCtx,
  field: keyof RawBboxCorners,
  value: number,
  min: number,
  max: number,
): void {
  if (value < min || value > max) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${field} must be between ${min} and ${max}`,
      path: [field],
    });
  }
}

/**
 * Rejects a reversed or degenerate bbox (min >= max on either axis) —
 * required so "reversed bounding-box coordinates" is rejected per spec.
 */
function checkBboxOrdering(ctx: z.RefinementCtx, value: RawBboxCorners): void {
  if (value.minLon >= value.maxLon) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'minLon must be less than maxLon',
      path: ['minLon'],
    });
  }
  if (value.minLat >= value.maxLat) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'minLat must be less than maxLat',
      path: ['minLat'],
    });
  }
}

/**
 * Bbox query for the administrative-area geometry endpoint. Per-corner
 * coerced numeric params (mirrors transportLayers.listByBbox), plus explicit
 * range/ordering validation — missing, reversed, or out-of-range corners must
 * all be rejected with 400 ("Invalid bounding box is rejected").
 */
export const AdminBoundaryBboxQuerySchema = z
  .object({
    minLon: z.coerce.number(),
    minLat: z.coerce.number(),
    maxLon: z.coerce.number(),
    maxLat: z.coerce.number(),
  })
  .superRefine((value, ctx) => {
    checkCornerRange(ctx, 'minLon', value.minLon, LONGITUDE_MIN, LONGITUDE_MAX);
    checkCornerRange(ctx, 'maxLon', value.maxLon, LONGITUDE_MIN, LONGITUDE_MAX);
    checkCornerRange(ctx, 'minLat', value.minLat, LATITUDE_MIN, LATITUDE_MAX);
    checkCornerRange(ctx, 'maxLat', value.maxLat, LATITUDE_MIN, LATITUDE_MAX);
    checkBboxOrdering(ctx, value);
  });

export type AdminBoundaryBboxQuery = z.infer<typeof AdminBoundaryBboxQuerySchema>;
